/**
 * The text fields of the personal-profile section editor.
 *
 * WHY THESE ARE AT MODULE SCOPE
 * -----------------------------
 * `edit-profile.tsx` declared its field component inside the screen function.
 * A function created in a render body is a new value every render, so
 * `<EditableInput/>` was a new component TYPE each time and React unmounted and
 * remounted the `TextInput` on every keystroke — the keyboard dropped and the
 * cursor was lost after each character. Declaring fields here, once, is the
 * fix; `src/lib/profile/__tests__/text-input-identity.test.ts` fails if any
 * component rendering a `TextInput` is nested again, anywhere in `src/`.
 *
 * WHY THE KEYBOARD STAYS UP
 * -------------------------
 * A single-line `TextInput` defaults to `submitBehavior: 'blurAndSubmit'`, so
 * pressing Return closes the keyboard and the user has to tap the next field to
 * reopen it. The fields in a section are chained instead: every field but the
 * last gets `returnKeyType="next"` and `submitBehavior="submit"` — which fires
 * the submit event WITHOUT blurring — and its handler focuses the following
 * input, so the keyboard never leaves. Only the last field carries
 * `returnKeyType="done"` with `blurAndSubmit`, letting the keyboard close.
 *
 * The multiline field is deliberately NOT chained: Return has to insert a
 * newline there, so it keeps the default behaviour and the user dismisses the
 * keyboard themselves.
 *
 * WHY THE AUTOFILL HINTS
 * ----------------------
 * Before this, `textContentType` and `inputMode` appeared 0 times across 184
 * `TextInput`s in the app, and `autoComplete` 6 times. A profile form is
 * precisely where the OS should be offering the user their own name, city and
 * URL. The hints are derived from the field KIND so a caller cannot forget them.
 */

import React, { forwardRef } from 'react';
import { View, Text, TextInput, Pressable, type TextInputProps } from 'react-native';

import { useThemeColors } from '@/lib/useThemeColors';

/**
 * What a field means, which is what decides its keyboard, capitalisation and
 * autofill contract. Callers pick a kind, never the individual flags.
 */
export type ProfileFieldKind = 'name' | 'jobTitle' | 'location' | 'url' | 'longText';

type AutofillSpec = Pick<
  TextInputProps,
  'textContentType' | 'autoComplete' | 'keyboardType' | 'autoCapitalize' | 'autoCorrect'
>;

const AUTOFILL: Record<ProfileFieldKind, AutofillSpec> = {
  name: {
    textContentType: 'name',
    autoComplete: 'name',
    autoCapitalize: 'words',
    autoCorrect: false,
  },
  jobTitle: {
    textContentType: 'jobTitle',
    autoComplete: 'off',
    autoCapitalize: 'words',
    autoCorrect: false,
  },
  location: {
    textContentType: 'addressCity',
    autoComplete: 'postal-address-locality',
    autoCapitalize: 'words',
    autoCorrect: false,
  },
  url: {
    textContentType: 'URL',
    autoComplete: 'off',
    keyboardType: 'url',
    autoCapitalize: 'none',
    autoCorrect: false,
  },
  longText: {
    textContentType: 'none',
    autoComplete: 'off',
    autoCapitalize: 'sentences',
    autoCorrect: true,
  },
};

export interface ProfileTextFieldProps {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  kind?: ProfileFieldKind;
  error?: string;
  required?: boolean;
  /** Helper text under the input, for things like URL format. */
  hint?: string;
  /**
   * Focus moves here when the user presses Next. Omit on the final field, which
   * then shows Done and lets the keyboard close.
   */
  onSubmitEditing?: () => void;
}

export const ProfileTextField = forwardRef<TextInput, ProfileTextFieldProps>(
  function ProfileTextField(
    { label, value, onChangeText, placeholder, kind = 'name', error, required, hint, onSubmitEditing },
    ref
  ) {
    const colors = useThemeColors();
    const multiline = kind === 'longText';
    // A chained field must not blur, or the keyboard closes between fields.
    const chained = !multiline && Boolean(onSubmitEditing);

    return (
      <View className="mb-4">
        <Text
          style={{ color: colors.text.secondary }}
          className="text-sm font-medium mb-1.5"
        >
          {label}
          {required && <Text style={{ color: colors.error }}> *</Text>}
        </Text>

        <TextInput
          ref={ref}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.text.tertiary}
          multiline={multiline}
          accessibilityLabel={label}
          returnKeyType={chained ? 'next' : 'done'}
          // `submitBehavior` OVERRIDES `blurOnSubmit` (RN 0.81), so only this one
          // is set — passing both would be contradictory. 'submit' fires the
          // submit event WITHOUT blurring, which is what keeps the keyboard up
          // as focus moves to the next field.
          submitBehavior={chained ? 'submit' : multiline ? 'newline' : 'blurAndSubmit'}
          onSubmitEditing={onSubmitEditing}
          {...AUTOFILL[kind]}
          className={`rounded-xl px-4 ${multiline ? 'py-3 min-h-[110px]' : 'py-3.5'}`}
          style={{
            backgroundColor: colors.surface.secondary,
            color: colors.text.primary,
            borderWidth: 1,
            borderColor: error ? colors.error : colors.border.light,
            fontSize: 16,
            textAlignVertical: multiline ? 'top' : 'center',
          }}
        />

        {error ? (
          <Text
            style={{ color: colors.error }}
            className="text-xs mt-1.5"
            accessibilityRole="alert"
          >
            {error}
          </Text>
        ) : hint ? (
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1.5">
            {hint}
          </Text>
        ) : null}
      </View>
    );
  }
);

/**
 * A row of choices rendered as pills — used for the company function title,
 * where the value is one of a known set rather than free text.
 *
 * Wraps, because a non-wrapping row of variable-width pills overflows on a
 * narrow screen (the `<layout_containment>` rule in CLAUDE.md).
 */
export function ProfileChoiceField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const colors = useThemeColors();

  return (
    <View className="mb-4">
      <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-1.5">
        {label}
      </Text>
      <View className="flex-row flex-wrap gap-2">
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <Pressable
              key={option.value}
              onPress={() => onChange(option.value)}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              accessibilityLabel={option.label}
              className="px-3 py-2 rounded-full"
              style={{
                backgroundColor: selected ? colors.primary[600] : colors.surface.secondary,
                borderWidth: 1,
                borderColor: selected ? colors.primary[600] : colors.border.light,
              }}
            >
              <Text
                style={{ color: selected ? '#FFFFFF' : colors.text.primary }}
                className="text-sm font-medium"
                numberOfLines={1}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
